import { useEffect, useRef, useState } from "react";
import { Upload, X, Check } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { inputCls, fullUrl } from "./shared";

export function MediaPicker({ onSelect, onClose }) {
  const [items, setItems] = useState([]);
  const [folders, setFolders] = useState([]);
  const [folder, setFolder] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    const params = {};
    if (folder) params.folder = folder;
    if (q) params.q = q;
    api.get("/admin/media", { params }).then((r) => { setItems(r.data.items); setFolders(r.data.folders); })
      .catch((e) => toast.error(formatApiError(e)));
  };
  useEffect(() => { load(); }, [folder]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("folder", folder || "Uncategorized");
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded to Media Library");
      load();
      setSelected(data.url);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-vv-black/50" onClick={onClose} />
      <div data-testid="media-picker" className="relative bg-white w-full max-w-3xl max-h-[85vh] flex flex-col p-6">
        <div className="flex items-center justify-between mb-5">
          <p className="font-display text-xl">Media Library</p>
          <button onClick={onClose} aria-label="Close" data-testid="media-picker-close"><X size={20} strokeWidth={1.25} /></button>
        </div>
        <div className="flex flex-wrap gap-2 mb-4">
          <button onClick={() => setFolder("")} className={`text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 border transition-colors ${!folder ? "bg-vv-black text-vv-ivory border-vv-black" : "border-vv-black/20"}`}>All</button>
          {folders.map((f) => (
            <button key={f} onClick={() => setFolder(f)} className={`text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 border transition-colors ${folder === f ? "bg-vv-black text-vv-ivory border-vv-black" : "border-vv-black/20"}`}>{f}</button>
          ))}
          <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex-1 min-w-40">
            <input data-testid="media-picker-search" value={q} onChange={(e) => setQ(e.target.value)} onBlur={load} placeholder="Search by name" className={inputCls} />
          </form>
          <button data-testid="media-picker-upload" onClick={() => fileRef.current?.click()} disabled={uploading} className="vv-btn-outline-dark !px-4">
            <Upload size={12} /> {uploading ? "Uploading…" : "Upload"}
          </button>
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={upload} data-testid="media-picker-upload-input" />
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3 overflow-y-auto flex-1">
          {items.map((m) => (
            <button key={m.id} data-testid={`media-item-${m.id}`} onClick={() => setSelected(m.url)}
              className={`relative aspect-square overflow-hidden border-2 transition-colors ${selected === m.url ? "border-vv-black" : "border-transparent hover:border-vv-black/30"}`}>
              <img src={fullUrl(m.url)} alt={m.name} className="w-full h-full object-cover" loading="lazy" />
              {selected === m.url && <span className="absolute top-1.5 right-1.5 bg-vv-black text-vv-ivory p-1"><Check size={12} /></span>}
              <span className="absolute bottom-0 inset-x-0 bg-vv-black/60 text-vv-ivory text-[8px] uppercase tracking-wider px-1.5 py-1 truncate">{m.name}</span>
            </button>
          ))}
          {items.length === 0 && <p className="col-span-full text-sm text-vv-muted py-10 text-center">No assets here yet — upload one.</p>}
        </div>
        <div className="flex justify-end gap-3 mt-5 pt-4 border-t border-vv-black/10">
          <button onClick={onClose} className="vv-btn-outline-dark">Cancel</button>
          <button data-testid="media-picker-use" disabled={!selected} onClick={() => onSelect(selected)}
            className="vv-btn-dark disabled:opacity-40">Use Selected Image</button>
        </div>
      </div>
    </div>
  );
}
